import React, { useState, useEffect, useRef } from "react";

function ActionCard({
  selectedStudent,
  taskList,
  selectedTasks,
  selectAll,
  showTaskList,
  cardPosition,
  actionCardRef,
  handleCloseCard,
  setShowTaskList,
  handleTaskSelection,
  handleSelectAll,
  handleRetake,
  handleParentReport,
  handleTeacherReport,
  toAllStudentResults,
  toReportCards,
  setViewParent,
  setViewTeacher,
  assessmentName,
}) {
  const studentIdArray = Array.isArray(selectedStudent.id)
    ? selectedStudent.id
    : [selectedStudent.id];

  const isClassLevel = Array.isArray(selectedStudent.id) && selectedStudent.id.length > 1;

  const [retakeError, setRetakeError] = useState(false);
  const [localSelectAll, setLocalSelectAll] = useState(false);
  const [finalCardPosition, setFinalCardPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    setLocalSelectAll(selectAll);
  }, [selectAll]);

  useEffect(() => {
    if (actionCardRef.current) {
      const cardRect = actionCardRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newTop = cardPosition.top;
      let newLeft = cardPosition.left;

      // Adjust if it overflows on the right
      if (newLeft + cardRect.width > viewportWidth) {
        newLeft = viewportWidth - cardRect.width - 8; // 16px padding from the edge
      }

      // Adjust if it overflows on the bottom
      if (newTop + cardRect.height > viewportHeight) {
        newTop = cardPosition.top - cardRect.height;
      }

      // Ensure it doesn't go off the top or left of the screen
      if (newTop < 0) {
        newTop = 8; // 16px padding from the top
      }

      if (newLeft < 0) {
        newLeft = 8; // 16px padding from the left
      }

      setFinalCardPosition({ top: newTop, left: newLeft });
    }
  }, [cardPosition, actionCardRef]);

  const handleLocalSelectAll = () => {
    const newState = !localSelectAll;
    setLocalSelectAll(newState);
    handleSelectAll();
  };

  return (
    <div
      ref={actionCardRef}
      style={{
        position: "absolute",
        top: `${finalCardPosition.top}px`,
        left: `${finalCardPosition.left}px`,
        zIndex: 1000,
        display: "flex",
        width: "320px",
        padding: "16px",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "16px",
        flexShrink: 0,
        borderRadius: "8px",
        border: "1px solid #E5E7EB",
        background: "#FFF",
        boxShadow:
          "0px 10px 15px -3px rgba(0, 0, 0, 0.10), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
        visibility: finalCardPosition.top ? 'visible' : 'hidden' // Hide until position is calculated
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          alignSelf: "stretch",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "#000",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "24px",
          }}
        >
          Actions
        </p>
        <button
          onClick={handleCloseCard}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "16px",
          }}
        >
          ✕
        </button>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "16px",
          alignSelf: "stretch",
          overflowY: "auto",
        }}
      >
        {/* Retake Tasks Section - Only show for individual students */}
        {!isClassLevel && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <p
              style={{
                margin: 0,
                color: "#000",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "20px",
              }}
            >
              Retake tasks
            </p>
          </div>

          <button
            style={{
              display: "flex",
              padding: "8px 12px",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "8px",
              alignSelf: "stretch",
              borderRadius: "6px",
              border: "1px solid rgba(156, 163, 175, 1)",
              background: "rgba(156, 163, 175, 1)",
              color: "#FFF",
              cursor: "pointer",
            }}
            onClick={() => {
              setShowTaskList(!showTaskList);
            }}
          >
            <span>
              {showTaskList ? "Hide tasks" : "Select tasks to retake"}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>

          {showTaskList && taskList.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "8px",
                alignSelf: "stretch",
              }}
            >
              {taskList.map((task) => (
                <label
                  key={`${task.answerid}-${task.name}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    alignSelf: "stretch",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedTasks.includes(task.answerid)}
                    onChange={() => handleTaskSelection(task.answerid)}
                    style={{
                      width: "16px",
                      height: "16px",
                    }}
                  />
                  <span
                    style={{
                      color: "#000",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "20px",
                    }}
                  >
                    {task.name}
                  </span>
                </label>
              ))}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  alignSelf: "stretch",
                }}
              >
                <input
                  type="checkbox"
                  checked={localSelectAll}
                  onChange={handleLocalSelectAll}
                  style={{
                    width: "16px",
                    height: "16px",
                  }}
                />
                <span
                  style={{
                    color: "#000",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "20px",
                  }}
                >
                  All tasks
                </span>
              </label>
            </div>
          ) : (
            showTaskList && (
              <p style={{ textAlign: "center", color: "red" }}>
                ⚠️ No tasks found
              </p>
            )
          )}

          <button
            style={{
              display: "flex",
              padding: "8px 12px",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              alignSelf: "stretch",
              borderRadius: "6px",
              border: "1px solid #3B82F6",
              background: "#3B82F6",
              color: "#FFF",
              cursor: "pointer",
            }}
            onClick={() => {
              if (selectedTasks.length === 0) {
                setRetakeError(true);
                setTimeout(() => setRetakeError(false), 3000);
              } else {
                setRetakeError(false);
                handleRetake();
              }
            }}
          >
            Retake
          </button>

          {retakeError && (
            <div style={{
              color: "red",
              fontSize: "12px",
              textAlign: "center",
              width: "100%",
              marginTop: "-4px"
            }}>
              ⚠️ Please select at least one task to retake
            </div>
          )}
        </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <p
              style={{
                margin: 0,
                color: "#000",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "20px",
              }}
            >
              {assessmentName?.match(/\b(Baseline|Midpoint|End)\b/i)?.[0] ||
                assessmentName?.split(" ")[1]} Report Cards
            </p>
          </div>

          <div
            style={{
              display: "flex",
              padding: "8px 12px",
              alignItems: "center",
              gap: "8px",
              alignSelf: "stretch",
              borderRadius: "6px",
              border: "1px solid #E5E7EB",
            }}
          >
            <input
              type="text"
              value={selectedStudent.name}
              readOnly
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                color: "#000",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "20px",
              }}
            />
            <button
              onClick={handleCloseCard}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              ✕
            </button>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              alignSelf: "stretch",
            }}
          >
            <button
              className="blue-action-button"
              onClick={() => {
                setViewParent(true);
                setViewTeacher(false);
                toAllStudentResults(studentIdArray, true, false);
              }}
            >
              Simple
            </button>
            <button
              className="blue-action-button"
              onClick={() => {
                setViewParent(false);
                setViewTeacher(true);
                toAllStudentResults(studentIdArray, false, true);
              }}
            >
              Detailed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ActionCard;
